import { GAME_HEIGHT } from "./constants"

// ── Prop types ────────────────────────────────────────────────────────────────

/** Every distinct scenery/hazard object in the game. */
export type PropType =
  | "lunar-rock-sm"
  | "lunar-rock-lg"
  | "crystal-cluster"
  | "energy-conduit"
  | "debris-chunk"

// ── Prop definition ───────────────────────────────────────────────────────────

export interface PropBodyConfig {
  shape: "rect" | "circle"
  width?: number
  height?: number
  radius?: number
  offsetX?: number
  offsetY?: number
}

export interface PropDefinition {
  type: PropType
  assetPath: string
  texture: string
  spriteSize: { w: number; h: number }
  body?: PropBodyConfig // omit for purely visual (no collision)
  damage?: number // contact damage to player (omit = non-hazard)
  scrollSpeed: number // base vx (negative = moves left)
  depth: number
  alpha?: number
  spawnYMin?: number // constrain vertical spawn range
  spawnYMax?: number
  tint?: number
}

export const PROP_DEFINITIONS: Record<PropType, PropDefinition> = {
  "lunar-rock-sm": {
    type: "lunar-rock-sm",
    assetPath: "backgrounds/sectors/moon/layer-2.webp",
    texture: "prop_lunar_rock_sm",
    spriteSize: { w: 64, h: 40 },
    body: { shape: "rect", width: 52, height: 28, offsetX: 6, offsetY: 6 },
    scrollSpeed: -1.4,
    depth: 55,
    spawnYMin: GAME_HEIGHT * 0.5,
    spawnYMax: GAME_HEIGHT * 0.85,
  },
  "lunar-rock-lg": {
    type: "lunar-rock-lg",
    assetPath: "backgrounds/sectors/moon/layer-2.webp",
    texture: "prop_lunar_rock_lg",
    spriteSize: { w: 120, h: 72 },
    body: { shape: "rect", width: 100, height: 54, offsetX: 10, offsetY: 9 },
    scrollSpeed: -1.0,
    depth: 56,
    spawnYMin: GAME_HEIGHT * 0.55,
    spawnYMax: GAME_HEIGHT * 0.9,
  },
  "crystal-cluster": {
    type: "crystal-cluster",
    assetPath: "backgrounds/sectors/moon/layer-2.webp",
    texture: "prop_crystal_cluster",
    spriteSize: { w: 80, h: 96 },
    scrollSpeed: -1.2,
    depth: 54,
    tint: 0x88ccff,
    spawnYMin: GAME_HEIGHT * 0.45,
    spawnYMax: GAME_HEIGHT * 0.8,
  },
  "energy-conduit": {
    type: "energy-conduit",
    assetPath: "backgrounds/sectors/core-perimeter/layer-1.webp",
    texture: "prop_energy_conduit",
    spriteSize: { w: 16, h: GAME_HEIGHT },
    body: {
      shape: "rect",
      width: 10,
      height: GAME_HEIGHT,
      offsetX: 3,
      offsetY: 0,
    },
    damage: 12,
    scrollSpeed: -1.6,
    depth: 60,
    tint: 0x00ffcc,
  },
  "debris-chunk": {
    type: "debris-chunk",
    assetPath: "backgrounds/sectors/moon/layer-2.webp",
    texture: "prop_debris_chunk",
    spriteSize: { w: 32, h: 28 },
    scrollSpeed: -2.2,
    depth: 53,
    alpha: 0.85,
  },
}

// ── Spawn presets ─────────────────────────────────────────────────────────────

/** Describes a group of props to scatter across a zone. */
export interface PropSpawnPreset {
  propType: PropType
  count: number
  xSpread: number // horizontal spacing between each
  yJitter: number // random Y variance per prop
}

/** Presets for scripted wave events. */
export const PROP_PRESETS: Record<string, PropSpawnPreset[]> = {
  "lunar-rubble-field": [
    { propType: "lunar-rock-sm", count: 4, xSpread: 140, yJitter: 60 },
    { propType: "lunar-rock-lg", count: 2, xSpread: 240, yJitter: 40 },
  ],
  "crystal-garden": [
    { propType: "crystal-cluster", count: 3, xSpread: 180, yJitter: 80 },
    { propType: "lunar-rock-sm", count: 2, xSpread: 100, yJitter: 30 },
  ],
  "debris-shower": [
    { propType: "debris-chunk", count: 6, xSpread: 80, yJitter: 140 },
  ],
}
